import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Layer,
    Popup,
    FocusTrapZone,
    IconButton,
    DefaultButton,
    Icon,
    TooltipHost,
    DirectionalHint,
    PrimaryButton,
    Overlay,
    mergeStyleSets
} from "@fluentui/react";
import styles from "./DynamicTemplatePopup.module.css";
import { saveTemplate, loadTemplate, updateTemplate, deleteTemplate, makeUserFavouriteTemplate, getPromptInstructionsToRender } from "../../api";
import "./TemplateEditor.css";
import i18next from "i18next";
import {
    Backdrop,
    Checkbox,
    Chip,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    Snackbar,
    SnackbarCloseReason,
    Tooltip,
    tooltipClasses,
    TooltipProps
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { styled } from "@mui/material/styles";
import { userInfo, isAdmin } from "../../authConfig";
import { setUserDefaultContentInStore, setDefaultTemplateIdInStore } from "../../Store/TemplateContents";
import { useDispatch, useSelector } from "react-redux";
import StarIcon from "@mui/icons-material/Star";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { selectSelectedProduct, selectSystemDefaultContent, selectTemplateContents } from "../../Store";
import Warning from "@mui/icons-material/Warning";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Alert from "@mui/material/Alert";
import {
    applyTemplatePayloadState,
    extractPayloadError,
    groupTemplateContentsByProduct,
    hasTemplateReadFailure,
    hasUserFavouriteReadFailure,
    parseTemplatePayload,
    resolveEffectiveTemplateForProduct,
    syncUserFavouriteFromPayload
} from "./utils";

type Product = {
    manual_key: string;
    faq_key: string;
    text: string;
    data?: Product[];
};

type TokenValues = {
    isInchargeOfAiWorks: string;
    userSupportId: string;
};

type PromptTargetArea = "prompt" | "promptInstructions";

export const userSupportId = "bot@ai-works";

interface ResponseTemplatePopupProps {
    hideDynamicTemplatePopup: boolean;
    selectedTemplate: number;
    handleTemplateIconClick: () => void;
    handleTemplateClick: (templateId: number) => void;
    handleTemplateContents: (contents: string, tempId: string) => void;
    handleTemplateContentsBySuggestions?: (contents: string, tempId: string) => void;
    handleClearTemplateContent: () => void;
    products: Product[];
    selectedProduct: string;
    snackbar: () => void;
    setIsEdited: (isEdit: boolean) => void;
}

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0
    },
    "&::before": {
        display: "none"
    }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "1.3rem" }} />} {...props} />
))(({ theme }) => ({
    // backgroundColor: "#f5f5f5",
    fontSize: "1rem",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
        transform: "rotate(90deg)"
    },
    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1)
    },
    ...theme.applyStyles("dark", {
        backgroundColor: "rgba(255, 255, 255, .05)"
    })
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)"
}));

const DynamicTemplatePrompt: React.FC<ResponseTemplatePopupProps> = ({
    hideDynamicTemplatePopup,
    selectedTemplate: selectedTemplateProp,
    handleTemplateIconClick,
    handleTemplateClick,
    handleTemplateContents,
    handleTemplateContentsBySuggestions,
    handleClearTemplateContent,
    products,
    selectedProduct,
    snackbar,
    setIsEdited
}) => {
    const [isEdit, setIsEdit] = useState(false);
    const [isAdd, setIsAdd] = useState(false);
    const [isCopyAndCreateNew, setIsCopyAndCreateNew] = useState(false);
    const [templateContents, setTemplateContents] = useState<any>([
        {
            id: "",
            pk: "",
            product: "",
            template: [{ title: "", prompt: "", fields: {}, promptInstructions: "", previewPrompt: "", isSystemTemplate: false, isDefault: false }],
            useCount: 0
        }
    ]);
    const [editingIndex, setEditingIndex] = useState<number>(-1);
    const [editingTemplate, setEditingTemplate] = useState<string>("");
    const [selectedTemplate, setSelectedTemplate] = useState<number>(selectedTemplateProp);
    const [userPromptValues, setUserPromptValues] = useState([
        { title: "", prompt: "", fields: {}, promptInstructions: "", previewPrompt: "", isSystemTemplate: false, isDefault: false }
    ]);
    const [fields, setFields] = useState<any>({});
    const [deleteConfirmationDialogVisible, setDeleteConfirmationDialogVisible] = useState(false);
    const [ConfirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
    const [currentId, setCurrentId] = useState<string | undefined>("");
    const [templateProduct, setTemplateProduct] = useState<string>("");
    const [isSnackOpen, setIsSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackBgColor, setSnackBgColor] = useState("green");
    const [snackFontColor, setSnackFontColor] = useState("white");

    const [clickedItem, setClickedItem] = useState("");
    const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showInfo, setShowInfo] = useState(false);
    const [isStarSelected, setIsStarSelected] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [pendingDeleteRefreshPayload, setPendingDeleteRefreshPayload] = useState<any | null>(null);

    const clickedTemplateTitle = useRef<string>("");
    const clickedTemplateIndex = useRef<number>(-1);
    const clickedTemplateId = useRef<string>("");
    const clickedTemplateEmailId = useRef<string>("");
    const clickedTemplateProduct = useRef<string>("");
    const loadedEmptyTemplateProducts = useRef<Set<string>>(new Set());
    const deleteRequestInFlight = useRef(false);
    const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
    const [activeMutation, setActiveMutation] = useState<"save" | "delete" | "favourite" | null>(null);

    const dispatch = useDispatch();
    const systemDefaultContent = useSelector(selectSystemDefaultContent);
    const cachedTemplateContents = useSelector(selectTemplateContents);
    const cachedTemplateProduct = useSelector(selectSelectedProduct);
    const isMutationInProgress = activeMutation !== null;
    const isDialogBusy = loading || isMutationInProgress;

    useEffect(() => {
        if (selectedProduct === "ALL") {
            setExpandedPanel("ALL");
        } else {
            setExpandedPanel(null);
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (!hideDynamicTemplatePopup) {
            loadedEmptyTemplateProducts.current.clear();
        }
    }, [hideDynamicTemplatePopup]);

    useEffect(() => {
        setSelectedTemplate(selectedTemplateProp);
    }, [selectedTemplateProp]);

    useEffect(() => {
        if (!hideDynamicTemplatePopup) {
            return;
        }
        if (selectedProduct === `${i18next.t("chat.productFilterText")}`) {
            setLoading(false);
            setClickedItem("");
            setTemplateContents({ quickSearch: [] });
            setTemplateProduct("");
            return;
        }
        if (isAdd || isEdit || isCopyAndCreateNew) {
            return;
        }
        let active = true;

        const hasLoadedTemplatesForSelectedProduct = cachedTemplateProduct === selectedProduct && cachedTemplateContents.length > 0;
        const hasLoadedEmptyTemplatesForSelectedProduct =
            cachedTemplateProduct === selectedProduct && cachedTemplateContents.length === 0 && loadedEmptyTemplateProducts.current.has(selectedProduct);

        if (hasLoadedTemplatesForSelectedProduct || hasLoadedEmptyTemplatesForSelectedProduct) {
            setTemplateContents(groupTemplateContentsByProduct(cachedTemplateContents));
            setTemplateProduct(selectedProduct);
            setClickedItem("");
            setLoading(false);
            return () => {
                active = false;
            };
        }

        const getTemplateFromDb = async () => {
            setLoading(true);
            try {
                const templateResponse = await loadTemplate(selectedProduct);
                if (!templateResponse || typeof templateResponse.json !== "function" || templateResponse.ok === false) {
                    throw new Error("Invalid template load response");
                }
                const data = await parseTemplatePayload(templateResponse);
                if (active) {
                    const payloadError = extractPayloadError(data);
                    if (payloadError) {
                        throw new Error(payloadError);
                    }
                    const nextTemplateContents = applyTemplatePayloadState(data, selectedProduct, dispatch, setTemplateContents, setTemplateProduct);
                    if (!nextTemplateContents) {
                        throw new Error("Invalid template load payload");
                    }
                    const nextTemplateEntries =
                        nextTemplateContents && typeof nextTemplateContents === "object" && !Array.isArray(nextTemplateContents)
                            ? Object.entries(nextTemplateContents)
                                  .filter(([key, value]) => key !== "quickSearch" && Array.isArray(value))
                                  .flatMap(([, value]) => value as any[])
                            : [];

                    if (nextTemplateEntries.length === 0) {
                        loadedEmptyTemplateProducts.current.add(selectedProduct);
                    } else {
                        loadedEmptyTemplateProducts.current.delete(selectedProduct);
                    }
                }
            } catch (error) {
                console.error("Error fetching template data: ", error);
                if (active) {
                    loadedEmptyTemplateProducts.current.delete(selectedProduct);
                    setTemplateContents({ quickSearch: [] });
                    setTemplateProduct(selectedProduct);
                    setClickedItem("");
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        getTemplateFromDb();
        setClickedItem("");
        return () => {
            active = false;
        };
    }, [hideDynamicTemplatePopup, selectedProduct, cachedTemplateContents, cachedTemplateProduct, isAdd, isEdit, isCopyAndCreateNew]);

    const handleSnackBarClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === "clickaway") {
            return;
        }

        setIsSnackOpen(false);
    };

    const handleAddUpdateSnackBar = (action: string, hasRefreshWarning = false) => {
        setSnackMessage(
            hasRefreshWarning
                ? action === "add"
                    ? i18next.t("template.templateSavedRefreshWarning")
                    : i18next.t("template.templateUpdatedRefreshWarning")
                : action === "add"
                  ? i18next.t("template.templateSaved")
                  : i18next.t("template.templateUpdated")
        );
        setSnackBgColor(hasRefreshWarning ? "#f5a623" : "green");
        setSnackFontColor(hasRefreshWarning ? "black" : "white");
        setIsSnackOpen(true);
    };

    const handleDeleteSnackBar = (hasRefreshWarning = false) => {
        setSnackMessage(hasRefreshWarning ? i18next.t("template.templateDeletedRefreshWarning") : i18next.t("template.templateDeleted"));
        setSnackBgColor(hasRefreshWarning ? "#f5a623" : "red");
        setSnackFontColor(hasRefreshWarning ? "black" : "white");
        setIsSnackOpen(true);
    };

    const handleDeleteErrorSnackBar = (message?: string) => {
        setSnackMessage(message ?? i18next.t("template.templateDeleteFailed"));
        setSnackBgColor("red");
        setSnackFontColor("white");
        setIsSnackOpen(true);
    };

    const handleFavouriteErrorSnackBar = (message?: string) => {
        setSnackMessage(message ?? i18next.t("template.favouriteUpdateFailed"));
        setSnackBgColor("red");
        setSnackFontColor("white");
        setIsSnackOpen(true);
    };

    const handleAddUpdateErrorSnackBar = (action: "add" | "update", message?: string) => {
        setSnackMessage(message ?? (action === "add" ? i18next.t("template.templateSaveFailed") : i18next.t("template.templateUpdateFailed")));
        setSnackBgColor("red");
        setSnackFontColor("white");
        setIsSnackOpen(true);
    };

    const parseTemplateMutationResult = useCallback(async (response: Response | undefined, fallbackErrorMessage: string) => {
        if (!response || typeof response.json !== "function" || response.ok === false) {
            throw new Error(fallbackErrorMessage);
        }

        const result = await parseTemplatePayload(response);
        const payloadError = extractPayloadError(result);
        if (payloadError) {
            throw new Error(payloadError ?? fallbackErrorMessage);
        }

        return result;
    }, []);

    const refreshVisibleTemplateList = useCallback(
        async (showLoadingIndicator = true) => {
            if (showLoadingIndicator) {
                setLoading(true);
            }
            try {
                const refreshedResponse = await loadTemplate(selectedProduct);
                if (!refreshedResponse || refreshedResponse.ok === false) {
                    throw new Error("Invalid template refresh response");
                }
                const refreshedPayload = await parseTemplatePayload(refreshedResponse);
                const payloadError = extractPayloadError(refreshedPayload);
                if (payloadError) {
                    throw new Error(payloadError);
                }
                const refreshedTemplateContents = applyTemplatePayloadState(
                    refreshedPayload,
                    selectedProduct,
                    dispatch,
                    setTemplateContents,
                    setTemplateProduct
                );
                if (!refreshedTemplateContents) {
                    throw new Error("Invalid template refresh payload");
                }
                return { success: true, payload: refreshedPayload, templateContents: refreshedTemplateContents };
            } catch (error) {
                console.error("Error refreshing template data after mutation: ", error);
                return { success: false, payload: null, templateContents: null };
            } finally {
                if (showLoadingIndicator) {
                    setLoading(false);
                }
            }
        },
        [dispatch, selectedProduct]
    );

    useEffect(() => {
        if (!pendingDeleteRefreshPayload || deleteConfirmationDialogVisible) {
            return;
        }

        let active = true;

        const applyDeleteRefresh = async () => {
            const refreshResult = await refreshVisibleTemplateList(true);

            if (!active) {
                return;
            }

            if (refreshResult.success) {
                dispatchHandleTemplateContents(refreshResult.templateContents ?? undefined);
                handleDeleteSnackBar();
            } else {
                const hasReadFailure = hasTemplateReadFailure(pendingDeleteRefreshPayload);

                if (!hasReadFailure) {
                    const updatedTemplateContents = applyTemplatePayloadState(
                        pendingDeleteRefreshPayload,
                        selectedProduct,
                        dispatch,
                        setTemplateContents,
                        setTemplateProduct
                    );
                    dispatchHandleTemplateContents(updatedTemplateContents ?? undefined);
                    handleDeleteSnackBar();
                } else {
                    syncUserFavouriteFromPayload(pendingDeleteRefreshPayload);
                    dispatchHandleTemplateContents();
                    handleDeleteSnackBar(true);
                }
            }

            setPendingDeleteRefreshPayload(null);
            deleteRequestInFlight.current = false;
            setActiveMutation(null);
        };

        applyDeleteRefresh().catch(error => {
            console.error("Delete refresh failed: ", error);
            if (!active) {
                return;
            }
            handleDeleteErrorSnackBar();
            setPendingDeleteRefreshPayload(null);
            deleteRequestInFlight.current = false;
            setActiveMutation(null);
        });

        return () => {
            active = false;
        };
    }, [deleteConfirmationDialogVisible, dispatch, pendingDeleteRefreshPayload, refreshVisibleTemplateList, selectedProduct]);

    if (!hideDynamicTemplatePopup) {
        return null;
    }

    const handleDeleteIconClick = (id: string) => {
        if (isDialogBusy) {
            return;
        }
        setCurrentId(id);
        setDeleteConfirmationDialogVisible(true);
        clickedTemplateId.current = "";
    };

    const handleConfirmDelete = async () => {
        if (deleteRequestInFlight.current || currentId === null || currentId === undefined) {
            return;
        }

        deleteRequestInFlight.current = true;
        setActiveMutation("delete");

        try {
            const response = await deleteTemplate(currentId.toString(), selectedProduct);
            if (!response?.ok) {
                let errorMessage: string | undefined;
                try {
                    const errorPayload = await response.json();
                    if (errorPayload?.error) {
                        errorMessage = String(errorPayload.error);
                    }
                } catch {
                    // Keep fallback message if error payload is unavailable.
                }
                handleDeleteErrorSnackBar(errorMessage);
                deleteRequestInFlight.current = false;
                setActiveMutation(null);
                return;
            }

            const result = await parseTemplatePayload(response);
            const payloadError = extractPayloadError(result);
            if (payloadError) {
                handleDeleteErrorSnackBar(payloadError);
                deleteRequestInFlight.current = false;
                setActiveMutation(null);
                return;
            }

            setClickedItem("");
            setCurrentId(undefined);
            setPendingDeleteRefreshPayload(result);
            setDeleteConfirmationDialogVisible(false);
        } catch (error) {
            console.error("Delete template failed: ", error);
            handleDeleteErrorSnackBar();
            deleteRequestInFlight.current = false;
            setActiveMutation(null);
        }
    };

    const handleCancelDelete = () => {
        if (isDialogBusy) {
            return;
        }
        setDeleteConfirmationDialogVisible(false);
        setCurrentId(undefined);
    };

    const handleCancelConfirmation = () => {
        if (isDialogBusy) {
            return;
        }
        setConfirmationDialogVisible(false);
    };

    const handleEditClick = async (selectedString: string) => {
        setIsEdit(true);
        const { product, index } = splitData(selectedString);
        setEditingTemplate(product);
        setEditingIndex(parseInt(index));
        const selectedTemplateFields = templateContents[product][index].template[0].fields || {};
        setFields(selectedTemplateFields);
        const updatedTemplateContentData: Array<any> = Array.of(formTemplateContentData(templateContents[product][index].template[0]));
        setUserPromptValues(updatedTemplateContentData);
    };

    const handleCopyAndCreateNewClick = async (selectedString: string) => {
        setIsCopyAndCreateNew(true);
        const { product, index } = splitData(selectedString);
        setEditingIndex(parseInt(index));
        setEditingTemplate(product);
        const selectedTemplateFields = templateContents[product][index].template[0].fields || {};
        setFields(selectedTemplateFields);
        const updatedTemplate: Array<any> = [...templateContents[product][index].template];
        let templateValue: Array<any> = Array.of(formTemplateContentData(updatedTemplate[0]));

        if (!isAdmin) {
            templateValue = templateValue.map(template => ({
                ...template,
                isSystemTemplate: false
            }));
        }
        setUserPromptValues(templateValue);
    };
    const handleSaveClick = async () => {
        if (isDialogBusy) {
            return;
        }
        const action: "add" | "update" = isEdit ? "update" : "add";
        const fallbackErrorMessage = action === "add" ? i18next.t("template.templateSaveFailed") : i18next.t("template.templateUpdateFailed");

        setActiveMutation("save");
        try {
            // on save btn click remove the extra keys from the fields object which doesn't have renderedInputBoxes.
            const currentFields = { ...fields };
            const filteredFields = Object.keys(currentFields).reduce(
                (accumulator, currentKey) => {
                    if (renderedInputBoxKeys.includes(currentKey)) {
                        accumulator[currentKey] = currentFields[currentKey];
                    }
                    return accumulator;
                },
                {} as Record<string, any>
            );
            const normalizedPromptValue = {
                ...userPromptValues[0],
                fields: filteredFields
            };

            if (action === "update") {
                setUserPromptValues([normalizedPromptValue]);
                const response = await updateTemplate(
                    templateContents[editingTemplate!][editingIndex!].id,
                    templateContents[editingTemplate!][editingIndex!].pk,
                    [normalizedPromptValue],
                    templateProduct,
                    0
                );
                const result = await parseTemplateMutationResult(response, fallbackErrorMessage);
                let refreshSucceeded = true;
                if (templateProduct === selectedProduct) {
                    const updatedTemplateContents = applyTemplatePayloadState(result, templateProduct, dispatch, setTemplateContents, setTemplateProduct);
                    if (!updatedTemplateContents && !hasTemplateReadFailure(result)) {
                        throw new Error(fallbackErrorMessage);
                    }
                    refreshSucceeded = !!updatedTemplateContents;
                } else {
                    const refreshResult = await refreshVisibleTemplateList(true);
                    refreshSucceeded = refreshResult.success;
                }
                handleAddUpdateSnackBar("update", !refreshSucceeded);
            } else {
                const nextPromptValue = !isAdmin
                    ? { ...normalizedPromptValue, isDefault: false }
                    : { ...normalizedPromptValue, isDefault: !!normalizedPromptValue.isDefault };

                setUserPromptValues([nextPromptValue]);

                const response = await saveTemplate([nextPromptValue], templateProduct);
                const result = await parseTemplateMutationResult(response, fallbackErrorMessage);
                let refreshSucceeded = true;
                if (templateProduct === selectedProduct) {
                    const updatedTemplateContents = applyTemplatePayloadState(result, templateProduct, dispatch, setTemplateContents, setTemplateProduct);
                    if (!updatedTemplateContents && !hasTemplateReadFailure(result)) {
                        throw new Error(fallbackErrorMessage);
                    }
                    refreshSucceeded = !!updatedTemplateContents;
                } else {
                    const refreshResult = await refreshVisibleTemplateList(true);
                    refreshSucceeded = refreshResult.success;
                }
                handleAddUpdateSnackBar("add", !refreshSucceeded);

                setUserPromptValues([
                    {
                        title: "",
                        prompt: "",
                        fields: {},
                        promptInstructions: "",
                        previewPrompt: "",
                        isSystemTemplate: false,
                        isDefault: false
                    }
                ]);
            }

            setIsAdd(false);
            setIsEdit(false);
            setShowAlert(false);
            setIsCopyAndCreateNew(false);
            setClickedItem("");
        } catch (error) {
            console.error(`${action === "add" ? "Save" : "Update"} template failed: `, error);
            handleAddUpdateErrorSnackBar(action, error instanceof Error ? error.message : fallbackErrorMessage);
        } finally {
            setActiveMutation(null);
        }
    };

    const addTemplate = () => {
        setIsAdd(true);
        setFields({});
        const updatedUserPromptValues = [...userPromptValues];
        updatedUserPromptValues[0] = {
            ...updatedUserPromptValues[0],
            title: "",
            prompt: "",
            fields: {},
            promptInstructions: "",
            previewPrompt: "",
            isSystemTemplate: false,
            isDefault: false
        };
        setUserPromptValues(updatedUserPromptValues);
    };

    const sendTemplateContents = (contents: any, tempId: string) => {
        if (isDialogBusy) {
            return;
        }
        const updatedContents = { ...contents, useCount: contents.useCount + 1 };
        if (updatedContents.pk !== "default@ai-works") {
            updateTemplate(
                updatedContents.id,
                updatedContents.pk,
                updatedContents.template,
                updatedContents.product,
                Number.isSafeInteger(updatedContents.useCount) ? updatedContents.useCount : 0
            );
        }
        handleTemplateContents(updatedContents.template[0], tempId);
    };

    const handleChange = (e: { target: { value: string } }) => {
        const newValue = e.target.value;
        setUserPromptValues(prevValues => {
            const updatedValues = [...prevValues];
            updatedValues[0] = { ...updatedValues[0], prompt: newValue };
            return updatedValues;
        });
        parseText(newValue);
        updateParsedText("", "", newValue, "prompt");
    };

    const handleFieldChange = (e: any, key: any) => {
        const value = e.target.value;
        const updatedFields = { ...fields, [key]: value };
        const filteredFields = Object.keys(updatedFields).reduce(
            (accumulator, currentKey) => {
                if (renderedInputBoxKeys.includes(currentKey)) {
                    accumulator[currentKey] = updatedFields[currentKey];
                }
                return accumulator;
            },
            {} as Record<string, any>
        );

        setFields(filteredFields);
        updateParsedText(key, value, userPromptValues[0].prompt, "prompt");
    };

    const parseText = (inputText: any) => {
        const regex = /\{([^}]+)\}/g;
        const matches = inputText.match(regex) || [];
        const newFields: any = { ...fields };
        matches.forEach((match: string) => {
            const key = match.replace(/[{}]/g, "");
            if (key && !newFields[key]) {
                newFields[key] = "";
            }
        });
        setFields(newFields);
    };

    const updateParsedText = (key: string, value: string, promptText: string, targetPromptArea: PromptTargetArea) => {
        let updatedText = targetPromptArea === "prompt" ? promptText : userPromptValues[0].prompt;
        const promptInstructionsText = targetPromptArea === "prompt" ? userPromptValues[0].promptInstructions : promptText;
        Object.keys(fields).forEach(fieldKey => {
            let fieldValue = fieldKey === key ? value : fields[fieldKey];
            fieldValue = fieldValue === "" ? `{${fieldKey}}` : fieldValue;
            const regex = new RegExp(`\\{${fieldKey}\\}`, "g");
            updatedText = updatedText.replace(regex, fieldValue);
        });

        const object: any = {};
        if (key) {
            object[key] = value;
        }
        updatedText = String(updatedText).concat(promptInstructionsText);
        setUserPromptValues(prevValues => {
            const updatedValues = [...prevValues];
            updatedValues[0] = {
                ...updatedValues[0],
                fields: { ...fields, ...object },
                previewPrompt: updatedText
            };
            return updatedValues;
        });
    };

    function RenderPreviewText() {
        const promptValues: any = userPromptValues[0].prompt;
        const promptInstructionsValue = userPromptValues[0]?.hasOwnProperty("promptInstructions") ? userPromptValues[0].promptInstructions : "";
        const dynamicVariables = promptValues.match(/{([^{}]+)}/g);
        if (!dynamicVariables) {
            return String(promptValues).concat(getPromptInstructionsToRender(userPromptValues[0])).trim();
        }
        const dynamicValues = dynamicVariables
            .map((match: string) => {
                const variableName = match.slice(1, -1);
                const fieldValue = fields[variableName];
                if (fieldValue !== undefined && fieldValue !== "") {
                    return `<span style="font-weight: bold; color: red;">${fieldValue}</span>`;
                }
                return "";
            })
            .filter((value: string) => value !== "");

        const previewTextValue = String(dynamicValues.length > 0 ? dynamicValues.join(" ").trim() : promptValues)
            .concat(getPromptInstructionsToRender(userPromptValues[0]))
            .trim();
        return <div dangerouslySetInnerHTML={{ __html: previewTextValue }} />;
    }

    function RenderInputBox() {
        const inputKeys: Array<string> = [];
        const promptValues: any = userPromptValues[0].prompt;
        const regex = /{([^{}]+)}/g;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(promptValues)) !== null) {
            if (match[1]) {
                inputKeys.push(match[1]);
            }
        }
        return inputKeys;
    }

    const renderedInputBoxKeys = RenderInputBox();

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTitle = e.target.value;
        setUserPromptValues(prevValues => {
            const updatedValues = prevValues.map((value, index) => {
                if (index === 0) {
                    return {
                        ...value,
                        title: newTitle
                    };
                }
                return value;
            });
            return updatedValues;
        });
    };

    const popupStyles = mergeStyleSets({
        root: {
            backgroundcolor: "rgba(0, 0, 0, 0.5)",
            color: "black",
            bottom: "0",
            left: "0",
            position: "absolute",
            right: "0",
            top: "0"
        },
        content: {
            background: "white",
            left: "50%",
            maxWidth: window.innerWidth < 1000 ? "300px" : "1000px",
            padding: "1em 3em 2em 2em",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "10px",
            border: "2px solid",
            borderColor: "grey"
        }
    });

    function deepEqual(obj1: any, obj2: any) {
        if (obj1 === obj2) {
            return true;
        }
        if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
            return false;
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }

        return true;
    }

    const checkContentsEntered = () => {
        if (isAdd && userPromptValues[0].title === "" && userPromptValues[0].prompt === "" && selectedProduct === templateProduct) {
            setIsAdd(false);
        } else {
            if (isAdd) {
                setConfirmationDialogVisible(true);
            }
            if (isEdit || isCopyAndCreateNew) {
                if (
                    userPromptValues[0].title.trim() === templateContents[editingTemplate][editingIndex].template[0].title.trim() &&
                    (templateContents[editingTemplate][editingIndex].template[0]?.hasOwnProperty("promptInstructions")
                        ? userPromptValues[0].prompt === templateContents[editingTemplate][editingIndex].template[0].prompt &&
                          userPromptValues[0].promptInstructions === templateContents[editingTemplate][editingIndex].template[0].promptInstructions
                        : JSON.stringify(getPreviewAreaTextValue()) ===
                          JSON.stringify(templateContents[editingTemplate][editingIndex].template[0].previewPrompt)) &&
                    (!isAdmin ||
                        (userPromptValues[0].isSystemTemplate === templateContents[editingTemplate][editingIndex].template[0].isSystemTemplate &&
                            userPromptValues[0].isDefault === templateContents[editingTemplate][editingIndex].template[0].isDefault)) &&
                    deepEqual(userPromptValues[0].fields, templateContents[editingTemplate][editingIndex].template[0].fields) &&
                    selectedProduct === templateProduct
                ) {
                    setIsEdit(false);
                    setIsCopyAndCreateNew(false);
                } else {
                    setConfirmationDialogVisible(true);
                }
            }
        }
    };

    const handleChangeTemplateProduct = (event: SelectChangeEvent) => {
        setTemplateProduct(event.target.value as string);
    };

    const calculateFontsizeForTemplateProduct = () => {
        if (window.innerWidth < 1000) {
            return "10px";
        }
        if (window.innerWidth < 1150) {
            return "11px";
        }
        if (window.innerWidth < 1300) {
            return "13px";
        }
        if (window.innerWidth < 1700) {
            return "14px";
        } else {
            return "15px";
        }
    };

    const calculateMarginRightForTemplateProduct = () => {
        if (window.innerWidth <= 1050) {
            return "24vw";
        }
        if (window.innerWidth <= 1400) {
            return "20vw";
        } else {
            return "18vw";
        }
    };

    const calculateMarginLeftForTemplateProduct = () => {
        if (window.innerWidth <= 1050) {
            return "17vw";
        }
        if (window.innerWidth <= 1400) {
            return "21vw";
        } else {
            return "21vw";
        }
    };

    const handleCheckSystemTemplate = (event: any) => {
        if (!event.target.checked) {
            setUserPromptValues(prevValues => {
                const updatedValues = [...prevValues];
                updatedValues[0] = {
                    ...updatedValues[0],
                    isDefault: false
                };
                return updatedValues;
            });
        }
        setUserPromptValues(prevValues => {
            const updatedValues = [...prevValues];
            updatedValues[0] = {
                ...updatedValues[0],
                isSystemTemplate: event.target.checked
            };
            return updatedValues;
        });
    };

    const handleCheckFaviTemplate = (event: any) => {
        setShowAlert(prev => !prev);
        if (event.currentTarget.checked) {
            dispatch(setDefaultTemplateIdInStore(clickedTemplateId.current));
        }
        setUserPromptValues(prevValues => {
            const updatedValues = [...prevValues];
            updatedValues[0] = {
                ...updatedValues[0],
                isDefault: event.target.checked
            };
            return updatedValues;
        });
    };

    const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: "rgba(0, 0, 0, 0.87)",
            boxShadow: theme.shadows[1],
            fontSize: 11
        }
    }));

    const splitData = (selectedString: string) => {
        const array = selectedString?.split("$");
        return { product: array[0], index: array[1] };
    };
    const productFilterPlaceholder = i18next.t("chat.productFilterText");

    const selectableProducts = Array.from(
        new Set(
            [templateProduct, selectedProduct, ...(products || []).map(product => product.text)].filter(
                (value): value is string => typeof value === "string" && value.trim() !== "" && value !== productFilterPlaceholder
            )
        )
    );

    const visibleTemplateTitles = Object.keys(templateContents).filter(title => {
        if (title === "quickSearch") {
            return false;
        }

        if (selectedProduct === "ALL") {
            return true;
        }

        if (title === selectedProduct) {
            return true;
        }

        return !templateContents[selectedProduct] && title === "ALL";
    });
    const selectedProductTemplateEntries = templateContents[selectedProduct];
    const selectedProductHasTemplates = Array.isArray(selectedProductTemplateEntries) && selectedProductTemplateEntries.length > 0;

    const getRequestStatusMessage = () => {
        if (activeMutation === "save") {
            return i18next.t("template.savingTemplateStatus");
        }

        if (activeMutation === "delete") {
            return deleteConfirmationDialogVisible ? i18next.t("template.deletingTemplateStatus") : i18next.t("template.refreshingTemplatesStatus");
        }

        if (activeMutation === "favourite") {
            return i18next.t("template.updatingFavouriteStatus");
        }

        if (loading) {
            return i18next.t("template.loadingTemplatesStatus");
        }

        return "";
    };

    const dispatchHandleTemplateContents = (templateCollections: any = templateContents) => {
        const applyTemplateInPopup = handleTemplateContentsBySuggestions ?? handleTemplateContents;
        const flattenedTemplateCollections = Array.isArray(templateCollections)
            ? templateCollections
            : Object.values(templateCollections || {})
                  .filter(Array.isArray)
                  .flatMap(value => value as any[]);
        const { templateEntry, templateId } = resolveEffectiveTemplateForProduct(selectedProduct, userInfo["userFavourite"], flattenedTemplateCollections);
        if (templateEntry && templateId) {
            applyTemplateInPopup(templateEntry.template[0], templateId);
            return;
        }
        handleClearTemplateContent();
    };

    const handleLikeClick = async (product: string, templateId: any) => {
        if (isDialogBusy) {
            return;
        }
        const isAllFallbackProductContext = selectedProduct !== "ALL" && product === "ALL" && !selectedProductHasTemplates;
        const activeProduct = isAllFallbackProductContext ? "ALL" : selectedProduct;
        const currentFavourite = userInfo["userFavourite"]?.[activeProduct];
        let nextFavourite: string | null = null;

        if (product !== activeProduct) {
            const allOptionDefaultContent = (templateContents["ALL"] || []).filter((temp: any) => temp.template[0].isDefault === true);
            const allDefaultId = allOptionDefaultContent?.[0]?.id;
            if (!allDefaultId) {
                return;
            }
            nextFavourite =
                templateId === allDefaultId
                    ? currentFavourite === templateId
                        ? "None"
                        : templateId
                    : currentFavourite === templateId
                      ? allDefaultId
                      : templateId;
        } else if (templateId === null) {
            nextFavourite = "None";
        } else {
            nextFavourite = currentFavourite === templateId ? "None" : templateId;
        }

        if (nextFavourite === null) {
            return;
        }
        setActiveMutation("favourite");
        try {
            const response = await makeUserFavouriteTemplate(nextFavourite, activeProduct, false);
            if (!response?.ok) {
                let errorMessage: string | undefined;
                if (typeof response?.json === "function") {
                    try {
                        const errorPayload = await response.json();
                        if (errorPayload?.error) {
                            errorMessage = String(errorPayload.error);
                        }
                    } catch {
                        // Keep fallback message if error payload is unavailable.
                    }
                }
                handleFavouriteErrorSnackBar(errorMessage);
                return;
            }

            const result = await parseTemplatePayload(response);
            const payloadError = extractPayloadError(result);
            if (payloadError) {
                handleFavouriteErrorSnackBar(payloadError);
                return;
            }

            if (!result?.userFavourite || typeof result.userFavourite !== "object" || hasUserFavouriteReadFailure(result)) {
                userInfo.userFavourite = {
                    ...(userInfo.userFavourite ?? {}),
                    [activeProduct]: nextFavourite
                };
            }
            if (hasTemplateReadFailure(result)) {
                syncUserFavouriteFromPayload(result);
            } else {
                applyTemplatePayloadState(result, selectedProduct, dispatch, setTemplateContents, setTemplateProduct);
            }
            dispatch(setUserDefaultContentInStore(""));
            dispatchHandleTemplateContents();
        } catch (error) {
            console.error("Update favourite template failed: ", error);
            handleFavouriteErrorSnackBar();
        } finally {
            setActiveMutation(null);
        }
    };

    const calculateFontsizeForFabIcons = () => {
        if (window.outerWidth < 1160) {
            return "12px";
        }
        if (window.outerWidth < 1290) {
            return "16px";
        }
        if (window.outerWidth < 1370) {
            return "15px";
        }
        if (window.outerWidth < 1450) {
            return "18px";
        }
        if (window.outerWidth < 1700) {
            return "20px";
        } else {
            return "20px";
        }
    };

    const isAccordionView = isAdmin && selectedProduct === "ALL";
    const Element = isAccordionView ? Accordion : "div";
    const ElementSummary = isAccordionView ? AccordionSummary : "div";
    const ElementDetails = isAccordionView ? AccordionDetails : "div";

    const handlePanelChange = (panel: any) => {
        setExpandedPanel(prevPanel => (prevPanel === panel ? null : panel)); // Toggle panel
    };

    const formTemplateContentData = (updatedTemplate: any) => {
        const templateValue: any = {
            title: updatedTemplate.title,
            prompt: updatedTemplate.hasOwnProperty("promptInstructions") ? updatedTemplate.prompt : "",
            fields: updatedTemplate.fields,
            promptInstructions: updatedTemplate.hasOwnProperty("promptInstructions") ? updatedTemplate.promptInstructions : updatedTemplate.prompt,
            previewPrompt: updatedTemplate.previewPrompt,
            isSystemTemplate: updatedTemplate.isSystemTemplate,
            isDefault: updatedTemplate.isDefault
        };
        return templateValue;
    };

    const handleChangeForPromptInstructions = (e: { target: { value: string } }) => {
        const newValue = e.target.value;
        setUserPromptValues(prevValues => {
            const updatedValues = [...prevValues];
            updatedValues[0] = { ...updatedValues[0], promptInstructions: newValue };
            return updatedValues;
        });
        updateParsedText("", "", newValue, "promptInstructions");
    };

    const getPreviewAreaTextValue = (): string => {
        let updatedText = userPromptValues[0].prompt;
        const promptInstructionsValue = userPromptValues[0].promptInstructions;
        Object.keys(fields).forEach(fieldKey => {
            let fieldValue = fields[fieldKey];
            fieldValue = fieldValue && fieldValue !== "" ? fieldValue : `{${fieldKey}}`;
            const regex = new RegExp(`\\{${fieldKey}\\}`, "g");
            updatedText = updatedText.replace(regex, fieldValue);
        });
        updatedText = String(updatedText).concat(promptInstructionsValue);
        return updatedText;
    };

    const getPromptTextWithDynamicValuesToRender = (template: any): string => {
        let updatedText = template?.prompt ?? "";
        Object.keys(template?.fields).forEach(fieldKey => {
            let fieldValue = template?.fields[fieldKey];
            fieldValue = fieldValue && fieldValue !== "" ? fieldValue : `{${fieldKey}}`;
            const regex = new RegExp(`\\{${fieldKey}\\}`, "g");
            updatedText = updatedText.replace(regex, fieldValue);
        });
        return updatedText;
    };

    return (
        <>
            <Dialog
                open={hideDynamicTemplatePopup}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    style: {
                        width: "100%",
                        maxWidth: window.outerWidth < 1050 ? "70%" : "77%",
                        minHeight: "40vh"
                    }
                }}
                disableEscapeKeyDown
            >
                <DialogTitle>
                    {!isAdd && !isEdit && !isCopyAndCreateNew && (
                        <>
                            <Icon iconName="Message" style={{ padding: "5px", fontSize: "20px" }} />
                            <span className={styles.headerFontSize}>{i18next.t("template.templateTitle")} </span>
                            {isAdmin && !loading && systemDefaultContent.length === 0 && (
                                <Tooltip title={i18next.t("template.noDefaultForSystemTemplate")}>
                                    <Chip
                                        icon={<Warning />}
                                        label={i18next.t("template.noDefaultForSystemTemplate")}
                                        variant="filled"
                                        sx={{
                                            cursor: "pointer",
                                            backgroundColor: "#e8f0b4",
                                            color: "#000",
                                            marginLeft: window.outerWidth < 1100 ? "10%" : window.outerWidth < 1600 ? "15%" : "20%",
                                            marginRight: window.outerWidth < 1100 ? "" : "10%",
                                            fontSize: window.outerWidth < 1100 ? "10px" : "15px",
                                            maxWidth:
                                                window.outerWidth < 1000 ? "20%" : window.outerWidth < 1200 ? "25%" : window.outerWidth < 1600 ? "30%" : "35%"
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <span style={{ float: "right" }}>
                                {!(isAdd || isEdit || isCopyAndCreateNew) && (
                                    <>
                                        <span>
                                            <TooltipHost
                                                content={
                                                    <table style={{ padding: "10px", width: "100%", borderCollapse: "collapse" }}>
                                                        <tbody>
                                                            <tr style={{ backgroundColor: "#f0efed", color: "rgb(25, 195, 125)", padding: "10px" }}>
                                                                <td style={{ padding: "8px" }}>
                                                                    {i18next.t("template.systemTemplate")}{" "}
                                                                    <span style={{ color: "red" }}>({i18next.t("template.default")})</span>
                                                                </td>
                                                                <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.systemDefaultInfo")}</td>
                                                            </tr>

                                                            <tr style={{ color: "rgb(25, 195, 125)" }}>
                                                                <td style={{ padding: "8px" }}>{i18next.t("template.systemTemplate")}</td>
                                                                <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.systemTemplateInfo")}</td>
                                                            </tr>

                                                            <tr style={{ backgroundColor: "#f0efed", color: "rgb(45, 63, 235)" }}>
                                                                <td style={{ padding: "8px" }}>{i18next.t("template.userTemplate")}</td>
                                                                <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.userTemplateInfo")}</td>
                                                            </tr>

                                                            <tr>
                                                                <td style={{ padding: "8px" }}>
                                                                    <StarIcon style={{ color: "red" }} />
                                                                </td>
                                                                <td style={{ padding: "8px", color: "black" }}> {i18next.t("template.defaultTemplateInfo")}</td>
                                                            </tr>

                                                            <tr style={{ backgroundColor: "#f0efed" }}>
                                                                <td style={{ padding: "8px" }}>
                                                                    <CheckCircle color="success" />
                                                                </td>
                                                                <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.applyInfo")}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                }
                                                directionalHint={DirectionalHint.bottomRightEdge}
                                            >
                                                <IconButton
                                                    style={{ color: "blueviolet" }}
                                                    title={i18next.t("template.info")}
                                                    disabled={isDialogBusy}
                                                    iconProps={{
                                                        iconName: "Info",
                                                        style: {
                                                            fontWeight: "bold",
                                                            fontSize: window.outerWidth < 1050 ? "10px" : "16px"
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        setShowInfo(!showInfo);
                                                    }}
                                                />
                                            </TooltipHost>
                                            <IconButton
                                                style={{ color: "blueviolet" }}
                                                title={i18next.t("template.edit")}
                                                disabled={
                                                    isDialogBusy ||
                                                    clickedItem === "" ||
                                                    (isAdmin && clickedTemplateProduct.current !== selectedProduct) ||
                                                    (!isAdmin &&
                                                        (clickedTemplateProduct.current !== selectedProduct ||
                                                            clickedTemplateEmailId.current === "default@ai-works"))
                                                }
                                                iconProps={{
                                                    iconName: "Edit",
                                                    style: {
                                                        fontWeight: "bold",
                                                        fontSize: window.outerWidth < 1050 ? "10px" : "16px"
                                                    }
                                                }}
                                                onClick={() => {
                                                    handleEditClick(`${clickedTemplateTitle.current}$${clickedTemplateIndex.current}`);
                                                    setTemplateProduct(clickedTemplateTitle.current);
                                                }}
                                            />
                                            <IconButton
                                                style={{ color: "blueviolet" }}
                                                title={i18next.t("template.copy")}
                                                disabled={isDialogBusy || clickedItem === ""}
                                                iconProps={{
                                                    iconName: "Copy",
                                                    style: {
                                                        fontWeight: "bold",
                                                        fontSize: window.outerWidth < 1050 ? "10px" : "16px"
                                                    }
                                                }}
                                                onClick={() => {
                                                    handleCopyAndCreateNewClick(`${clickedTemplateTitle.current}$${clickedTemplateIndex.current}`);
                                                    setTemplateProduct(selectedProduct);
                                                }}
                                            />
                                            <IconButton
                                                style={{ color: "blueviolet" }}
                                                title={i18next.t("template.delete")}
                                                disabled={
                                                    isDialogBusy ||
                                                    clickedItem === "" ||
                                                    (isAdmin && clickedTemplateProduct.current !== selectedProduct) ||
                                                    (!isAdmin &&
                                                        (clickedTemplateProduct.current !== selectedProduct ||
                                                            clickedTemplateEmailId.current === "default@ai-works"))
                                                }
                                                iconProps={{
                                                    iconName: "Delete",
                                                    style: {
                                                        fontWeight: "bold",
                                                        fontSize: window.outerWidth < 1050 ? "10px" : "16px"
                                                    }
                                                }}
                                                onClick={() => {
                                                    handleDeleteIconClick(clickedTemplateId.current);
                                                }}
                                            />
                                        </span>

                                        <IconButton
                                            autoFocus
                                            title={i18next.t("template.newTemplate")}
                                            disabled={isDialogBusy}
                                            iconProps={{ iconName: "Add", style: { fontSize: window.outerWidth < 1050 ? "10px" : "16px" } }}
                                            style={{ color: "blueviolet" }}
                                            onClick={() => addTemplate()}
                                        />
                                    </>
                                )}

                                <IconButton
                                    style={{ color: "blueviolet" }}
                                    title={i18next.t("template.close")}
                                    disabled={isDialogBusy}
                                    iconProps={{ iconName: "Cancel", style: { fontSize: window.outerWidth < 1050 ? "10px" : "16px" } }}
                                    onClick={() => {
                                        if (isDialogBusy) {
                                            return;
                                        }
                                        if (isAdd || isEdit || isCopyAndCreateNew) {
                                            setIsAdd(false);
                                            setIsEdit(false);
                                            setIsCopyAndCreateNew(false);
                                        } else {
                                            handleTemplateIconClick();
                                            setClickedItem("");
                                        }
                                    }}
                                />
                            </span>
                        </>
                    )}
                    <Snackbar
                        open={isSnackOpen}
                        autoHideDuration={1000}
                        onClose={handleSnackBarClose}
                        message={snackMessage}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                        ContentProps={{
                            style: { backgroundColor: snackBgColor, color: snackFontColor }
                        }}
                    />
                </DialogTitle>

                <DialogContent
                    className={isAdd || isEdit || isCopyAndCreateNew ? styles.TemplateContentContainerInAddEditMode : styles.TemplateContentContainer}
                    style={{ marginBottom: "16px", position: "relative" }}
                >
                    {isMutationInProgress && (
                        <Backdrop
                            open={true}
                            data-testid="template-request-overlay"
                            sx={{
                                position: "absolute",
                                color: "#fff",
                                zIndex: theme => theme.zIndex.modal + 1
                            }}
                        >
                            <div className={styles.requestStatusCard}>
                                <div className={styles.requestStatusLine} />
                                <div className={styles.requestStatusMessage}>{getRequestStatusMessage()}</div>
                                <div className={styles.requestStatusDots} aria-hidden="true">
                                    <span className={styles.requestStatusDot} />
                                    <span className={styles.requestStatusDot} />
                                    <span className={styles.requestStatusDot} />
                                </div>
                            </div>
                        </Backdrop>
                    )}
                    {isEdit || isAdd || isCopyAndCreateNew ? (
                        <div className={styles.templateContentInput}>
                            <div style={{ display: "flex" }}>
                                <div className={styles.inputDivs} style={{ width: "80%" }}>
                                    <h5
                                        style={{
                                            alignSelf: "center",
                                            width: window.innerWidth < 1500 ? "10.5%" : "12.3%",
                                            marginRight: window.innerWidth > 1500 ? "0px" : "16px"
                                        }}
                                    >
                                        {i18next.t("template.title")}
                                    </h5>
                                    <input
                                        value={userPromptValues[0].title}
                                        onChange={handleTitleChange}
                                        placeholder={i18next.t("template.titlePlaceHolder")}
                                        className={styles.templateTitle}
                                        disabled={isDialogBusy}
                                        autoFocus
                                    />
                                </div>
                                <div style={{ width: "20%", marginLeft: "5px" }} className={styles.inputDivs}>
                                    <div style={{ backgroundColor: "white", width: "100%", padding: "5px" }}>
                                        <FormControl fullWidth style={{ height: "100%" }}>
                                            <InputLabel id="product-select-label">{i18next.t("template.product")}</InputLabel>
                                            <Select
                                                labelId="product-select-label"
                                                id="product-select"
                                                label={i18next.t("template.product")}
                                                value={templateProduct}
                                                disabled={isDialogBusy}
                                                onChange={handleChangeTemplateProduct}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 200,
                                                            right: calculateMarginRightForTemplateProduct(),
                                                            left: calculateMarginLeftForTemplateProduct(),
                                                            fontSize: calculateFontsizeForTemplateProduct()
                                                        }
                                                    }
                                                }}
                                                style={{ margin: 0 }}
                                                renderValue={selected => (
                                                    <LightTooltip title={selected} arrow style={{ padding: "0px" }}>
                                                        <span>{selected}</span>
                                                    </LightTooltip>
                                                )}
                                            >
                                                {selectableProducts.map(productText => (
                                                    <MenuItem key={productText} value={productText} style={{ fontSize: calculateFontsizeForTemplateProduct() }}>
                                                        <LightTooltip title={productText} placement="right-end" followCursor>
                                                            <span>{productText}</span>
                                                        </LightTooltip>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.inputDivs}>
                                <h5 style={{ alignSelf: "center", width: "8%", marginRight: "10px" }}>{i18next.t("template.prompt")}</h5>
                                <textarea
                                    draggable={false}
                                    value={userPromptValues[0].prompt}
                                    onChange={handleChange}
                                    disabled={isDialogBusy}
                                    placeholder={i18next.t("template.promptPlaceHolder")}
                                    rows={10}
                                    cols={150}
                                    className={styles.templatePromptInput}
                                    style={{ fontFamily: "BIZ UDPGothic", width: "91%" }}
                                />
                            </div>
                            {renderedInputBoxKeys.length > 0 && (
                                <div className={styles.inputDivs}>
                                    <h5 style={{ paddingRight: "10px", alignSelf: "center", width: "10%" }}>{i18next.t("template.value")}</h5>
                                    <div className={styles.promptValues}>
                                        {renderedInputBoxKeys.map((key, index) => (
                                            <div className="fields" key={index} style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                                <label style={{ marginLeft: "20px", marginRight: "10px", alignSelf: "center" }}>{key}:</label>
                                                <input
                                                    type="text"
                                                    placeholder={`${key}...`}
                                                    value={fields[key] || ""}
                                                    disabled={isDialogBusy}
                                                    onChange={e => handleFieldChange(e, key)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className={styles.inputDivs}>
                                <h5 style={{ alignSelf: "center", width: "8%", marginRight: "10px" }}>{i18next.t("template.promptInstructions")}</h5>
                                <textarea
                                    draggable={false}
                                    value={userPromptValues[0].promptInstructions}
                                    onChange={handleChangeForPromptInstructions}
                                    disabled={isDialogBusy}
                                    placeholder={i18next.t("template.promptInstructionsPlaceHolder")}
                                    rows={10}
                                    cols={150}
                                    className={styles.templatePromptInput}
                                    style={{ fontFamily: "BIZ UDPGothic", width: "91%" }}
                                />
                            </div>
                            <div className={styles.previewDiv}>
                                <h5 style={{ paddingRight: "10px", alignSelf: "center", width: "12%" }}>{i18next.t("template.preview")}</h5>
                                <div className={styles.previewContent}>
                                    <RenderPreviewText />
                                </div>
                            </div>
                            {showAlert && (
                                <Alert severity="warning">
                                    {userPromptValues[0].isDefault ? i18next.t("template.warningByCheck") : i18next.t("template.warningByUnCheck")}
                                </Alert>
                            )}
                            {isAdmin && (
                                <div className={styles.previewDiv} style={{ justifyContent: "space-between" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={handleCheckSystemTemplate}
                                                checked={userPromptValues[0].isSystemTemplate}
                                                disabled={isDialogBusy}
                                            />
                                        }
                                        label={i18next.t("template.systemTemplateText")}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={!userPromptValues[0].isSystemTemplate || isDialogBusy}
                                                onChange={handleCheckFaviTemplate}
                                                checked={userPromptValues[0].isDefault}
                                            />
                                        }
                                        label={i18next.t("template.defaultTemplateText")}
                                    />
                                </div>
                            )}
                            {ConfirmationDialogVisible && (
                                <Layer>
                                    <Popup role="dialog" aria-modal="true" onDismiss={handleCancelConfirmation} className={popupStyles.root}>
                                        <Overlay />
                                        <FocusTrapZone>
                                            <div role="document" className={popupStyles.content}>
                                                <div>
                                                    <IconButton style={{ color: "red" }} iconProps={{ iconName: "WarningSolid" }} />
                                                    <IconButton
                                                        style={{ color: "green", float: "right" }}
                                                        iconProps={{ iconName: "Cancel" }}
                                                        disabled={isDialogBusy}
                                                        onClick={handleCancelConfirmation}
                                                    />
                                                    <span
                                                        style={{
                                                            padding: "5px",
                                                            fontSize: window.innerWidth < 1000 ? "12px" : "15px",
                                                            fontWeight: "bold"
                                                        }}
                                                    >
                                                        {i18next.t("template.confirmationTitle")}
                                                    </span>
                                                </div>
                                                <span style={{ marginTop: "25px", fontSize: window.innerWidth < 1000 ? "12px" : "15px" }}>
                                                    <h4> {i18next.t("template.confirmationContent")} </h4>
                                                </span>
                                                <div style={{ display: "flex", paddingTop: "25px", justifyContent: "flex-end" }}>
                                                    <PrimaryButton
                                                        onClick={() => {
                                                            if (isDialogBusy) {
                                                                return;
                                                            }
                                                            setConfirmationDialogVisible(false);
                                                            setTemplateProduct(selectedProduct);
                                                            setIsEdit(false);
                                                            setIsAdd(false);
                                                            setIsCopyAndCreateNew(false);
                                                            setEditingIndex(-1);
                                                        }}
                                                        text={i18next.t("template.confirm")}
                                                        disabled={isDialogBusy}
                                                    />
                                                    <DefaultButton
                                                        onClick={() => setConfirmationDialogVisible(false)}
                                                        text={i18next.t("template.cancel")}
                                                        disabled={isDialogBusy}
                                                        style={{ marginLeft: "10px" }}
                                                    />
                                                </div>
                                            </div>
                                        </FocusTrapZone>
                                    </Popup>
                                </Layer>
                            )}
                        </div>
                    ) : (
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                flexWrap: "wrap",
                                maxHeight: "70vh",
                                rowGap: "5vh",
                                columnGap: "5vh"
                            }}
                        >
                            {loading ? (
                                <div
                                    style={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "center", width: "100%", flexDirection: "column" }}
                                >
                                    <div>
                                        <div className={styles.loadingStatusMessage}>{getRequestStatusMessage()}</div>
                                        <Skeleton variant="text" sx={{ fontSize: "2rem", backgroundColor: "#f2f3f5" }} animation="wave" />
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "20px",
                                            justifyContent: "center",
                                            width: "100%",
                                            flexDirection: "row"
                                        }}
                                    >
                                        <Skeleton
                                            variant="rectangular"
                                            width={310}
                                            height={318}
                                            className={styles.skeletonTemplateContainer}
                                            animation="wave"
                                            style={{ backgroundColor: "#f2f3f5" }}
                                        />
                                        <Skeleton
                                            variant="rectangular"
                                            width={310}
                                            height={318}
                                            className={styles.skeletonTemplateContainer}
                                            animation="wave"
                                            style={{ backgroundColor: "#f2f3f5" }}
                                        />
                                        <Skeleton
                                            variant="rectangular"
                                            width={310}
                                            height={318}
                                            className={styles.skeletonTemplateContainer}
                                            animation="wave"
                                            style={{ backgroundColor: "#f2f3f5" }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                selectedProduct === templateProduct && (
                                    <div style={{ width: "100%" }}>
                                        <div>
                                            {showInfo && (
                                                <table className={styles.promptContent} style={{ padding: "10px", width: "100%", borderCollapse: "collapse" }}>
                                                    <tbody>
                                                        <tr style={{ backgroundColor: "#f0efed", color: "rgb(25, 195, 125)", padding: "10px" }}>
                                                            <td style={{ padding: "8px", textAlign: "center", verticalAlign: "middle" }} rowSpan={6}>
                                                                {i18next.t("template.info")}
                                                            </td>
                                                        </tr>
                                                        <tr style={{ backgroundColor: "#f0efed", color: "rgb(25, 195, 125)", padding: "10px" }}>
                                                            <td style={{ padding: "8px" }}>
                                                                {i18next.t("template.systemTemplate")}{" "}
                                                                <span style={{ color: "red" }}>({i18next.t("template.default")})</span>
                                                            </td>
                                                            <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.systemDefaultInfo")}</td>
                                                        </tr>

                                                        <tr style={{ color: "rgb(25, 195, 125)" }}>
                                                            <td style={{ padding: "8px" }}>{i18next.t("template.systemTemplate")}</td>
                                                            <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.systemTemplateInfo")}</td>
                                                        </tr>

                                                        <tr style={{ backgroundColor: "#f0efed", color: "rgb(45, 63, 235)" }}>
                                                            <td style={{ padding: "8px" }}>{i18next.t("template.userTemplate")}</td>
                                                            <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.userTemplateInfo")}</td>
                                                        </tr>

                                                        <tr>
                                                            <td style={{ padding: "8px" }}>
                                                                <StarIcon style={{ color: "red" }} />
                                                            </td>
                                                            <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.defaultTemplateInfo")}</td>
                                                        </tr>

                                                        <tr style={{ backgroundColor: "#f0efed" }}>
                                                            <td style={{ padding: "8px" }}>
                                                                <CheckCircle color="success" />
                                                            </td>
                                                            <td style={{ padding: "8px", color: "black" }}>{i18next.t("template.applyInfo")}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                        {visibleTemplateTitles.map(title => (
                                            <Element
                                                key={title}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px",
                                                    border: isAdmin && selectedProduct === "ALL" ? "1px solid black" : "",
                                                    marginBottom: "10px",
                                                    borderRadius: "10px",
                                                    cursor: "pointer"
                                                }}
                                                {...(isAccordionView
                                                    ? {
                                                          expanded: expandedPanel === title,
                                                          onChange: () => handlePanelChange(title),
                                                          defaultExpanded: selectedProduct === "ALL"
                                                      }
                                                    : {})}
                                            >
                                                <ElementSummary aria-controls="panel1-content" id={title}>
                                                    {isAdmin && selectedProduct === "ALL" ? (
                                                        <p style={{ textAlign: "center" }}>{title}</p>
                                                    ) : (
                                                        <h3 style={{ textAlign: "center", backgroundColor: "#f5f5f5" }}>{title}</h3>
                                                    )}
                                                </ElementSummary>
                                                {(isAdmin && selectedProduct === "ALL" ? expandedPanel === title : true) ? (
                                                    <ElementDetails
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            rowGap: "5vh",
                                                            columnGap: "5vh",
                                                            flexWrap: "wrap"
                                                        }}
                                                    >
                                                        {templateContents[title]?.map((e: any, i: number) => {
                                                            const isAllFallbackForSelectedProduct =
                                                                selectedProduct !== "ALL" && title === "ALL" && !selectedProductHasTemplates;
                                                            const favouriteLookupProduct =
                                                                selectedProduct === "ALL" ? title : isAllFallbackForSelectedProduct ? "ALL" : selectedProduct;
                                                            const favouriteLookupValue = userInfo?.userFavourite?.[favouriteLookupProduct];
                                                            const hasExplicitFavouriteForLookupProduct = !!(
                                                                favouriteLookupValue && favouriteLookupValue !== "None"
                                                            );
                                                            const hasFavouriteLookupProductKey =
                                                                userInfo?.userFavourite?.hasOwnProperty(favouriteLookupProduct) === true;
                                                            const isUserFavourite = hasExplicitFavouriteForLookupProduct && favouriteLookupValue === e.id;
                                                            const isDefaultFavourite = e.pk === "default@ai-works" && e.template[0].isDefault === true;

                                                            const isHoveredTemplate = hoveredTemplate === e.id && title === selectedProduct;

                                                            const isHoveredTemplateForAdmin = hoveredTemplate === e.id && title === "ALL";
                                                            const isHoveredTemplateForNonAdmin = hoveredTemplate === e.id;

                                                            const shouldDisplayFabForAdmin =
                                                                userInfo?.userFavourite?.hasOwnProperty(title) === false ||
                                                                userInfo?.userFavourite?.[title] === ""
                                                                    ? isDefaultFavourite || isHoveredTemplate
                                                                    : isUserFavourite || isHoveredTemplate;

                                                            const shouldDisplayFabForNonAdmin =
                                                                isHoveredTemplateForNonAdmin ||
                                                                (userInfo?.userFavourite?.[title] === undefined || userInfo?.userFavourite?.[title] === ""
                                                                    ? isDefaultFavourite
                                                                    : userInfo.userFavourite[title]
                                                                      ? isUserFavourite
                                                                      : false);

                                                            const shouldDisplayFabForAdminWithVaryProducts =
                                                                isHoveredTemplateForAdmin || (hasExplicitFavouriteForLookupProduct ? isUserFavourite : false);

                                                            const shouldDisplayFabForNonAdminWithVaryProducts =
                                                                isHoveredTemplateForNonAdmin ||
                                                                (hasExplicitFavouriteForLookupProduct ? isUserFavourite : false);

                                                            const adminsFab =
                                                                selectedProduct === "ALL"
                                                                    ? shouldDisplayFabForAdmin
                                                                    : title === selectedProduct
                                                                      ? shouldDisplayFabForAdmin
                                                                      : shouldDisplayFabForAdminWithVaryProducts;

                                                            const nonAdminsFab =
                                                                title === selectedProduct
                                                                    ? shouldDisplayFabForNonAdmin
                                                                    : shouldDisplayFabForNonAdminWithVaryProducts;

                                                            const shouldDisplayFab = isAdmin ? adminsFab : nonAdminsFab;
                                                            const templateIdentifier = `${e.id ?? "unknown"}-${e.pk ?? "unknown"}-${e.product ?? title}`;
                                                            const currentTemplateKey = `${title}-${templateIdentifier}`;
                                                            const reactTemplateKey = `${currentTemplateKey}-${i}`;
                                                            const isSystemTemplate = e.pk === "default@ai-works";
                                                            const isTemplateSelected = clickedItem === currentTemplateKey;
                                                            const selectedOuterColor = isSystemTemplate ? "#19c37d" : "#d3d3f2";

                                                            return (
                                                                <div
                                                                    key={reactTemplateKey}
                                                                    className={styles.templateZoom}
                                                                    style={{
                                                                        background: isTemplateSelected ? selectedOuterColor : ""
                                                                    }}
                                                                    onClick={() => {
                                                                        setClickedItem(currentTemplateKey);
                                                                        handleTemplateClick(i + 1);
                                                                        clickedTemplateTitle.current = title;
                                                                        clickedTemplateIndex.current = i;
                                                                        clickedTemplateId.current = e.id;
                                                                        clickedTemplateEmailId.current = e.pk;
                                                                        clickedTemplateProduct.current = e.product;
                                                                    }}
                                                                >
                                                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                                        <div
                                                                            className={styles.responseTemplateContainer}
                                                                            style={{
                                                                                border: "1px solid black",
                                                                                backgroundColor: "white",
                                                                                padding: "3%",
                                                                                resize: "none",
                                                                                display: "flex",
                                                                                flexDirection: "column"
                                                                            }}
                                                                            onMouseEnter={() => {
                                                                                setHoveredTemplate(e.id);
                                                                            }}
                                                                            onMouseLeave={() => {
                                                                                setHoveredTemplate(null);
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                    height: "13%"
                                                                                }}
                                                                            >
                                                                                <Tooltip title={e.template[0].title}>
                                                                                    <h4
                                                                                        style={{
                                                                                            color: e.pk === "default@ai-works" ? "#19c37d" : "#2d3feb"
                                                                                        }}
                                                                                        className={styles.promptTitle}
                                                                                    >
                                                                                        {e.template[0].title}
                                                                                    </h4>
                                                                                </Tooltip>

                                                                                <span>
                                                                                    {shouldDisplayFab && (
                                                                                        <Tooltip
                                                                                            title={
                                                                                                isUserFavourite ||
                                                                                                (!isAllFallbackForSelectedProduct &&
                                                                                                    e.template[0].isDefault &&
                                                                                                    userInfo?.userFavourite?.[title] === "")
                                                                                                    ? i18next.t("template.defaultTemplateTooltip")
                                                                                                    : i18next.t("template.clickToMakeDefaultTemplateTooltip")
                                                                                            }
                                                                                        >
                                                                                            <StarIcon
                                                                                                aria-label="like"
                                                                                                onClick={async (el: any) => {
                                                                                                    if (
                                                                                                        isAdmin &&
                                                                                                        title !== selectedProduct &&
                                                                                                        selectedProduct === "ALL"
                                                                                                    )
                                                                                                        return;
                                                                                                    el.stopPropagation();
                                                                                                    if (
                                                                                                        e.pk === "default@ai-works" &&
                                                                                                        e.template[0].isDefault &&
                                                                                                        (!userInfo?.userFavourite?.hasOwnProperty(title) ||
                                                                                                            userInfo?.userFavourite?.[title] === "")
                                                                                                    ) {
                                                                                                        await (title === selectedProduct
                                                                                                            ? handleLikeClick(title, null)
                                                                                                            : handleLikeClick(title, e.id));
                                                                                                    } else {
                                                                                                        if (
                                                                                                            e.pk === "default@ai-works" &&
                                                                                                            e.template[0].isDefault &&
                                                                                                            userInfo?.userFavourite?.[title] === e.id &&
                                                                                                            !isStarSelected
                                                                                                        ) {
                                                                                                            await handleLikeClick(title, null);
                                                                                                        } else {
                                                                                                            await handleLikeClick(title, e.id);
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                                style={{
                                                                                                    color:
                                                                                                        isUserFavourite ||
                                                                                                        (!isAllFallbackForSelectedProduct &&
                                                                                                            e.pk === "default@ai-works" &&
                                                                                                            e.template[0].isDefault &&
                                                                                                            (!hasFavouriteLookupProductKey ||
                                                                                                                userInfo?.userFavourite?.[
                                                                                                                    favouriteLookupProduct
                                                                                                                ] === "")) ||
                                                                                                        (selectedProduct === "ALL" && title !== selectedProduct)
                                                                                                            ? "red"
                                                                                                            : "grey",
                                                                                                    cursor:
                                                                                                        isAdmin &&
                                                                                                        title !== selectedProduct &&
                                                                                                        selectedProduct === "ALL"
                                                                                                            ? "not-allowed"
                                                                                                            : "pointer"
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                            {Array.isArray(e.template) &&
                                                                                e.template.map((element: any, elementIndex: number) => (
                                                                                    <div
                                                                                        className={styles.promptContent}
                                                                                        key={`${templateIdentifier}-prompt-${elementIndex}`}
                                                                                    >
                                                                                        {getPromptTextWithDynamicValuesToRender(element) +
                                                                                            getPromptInstructionsToRender(element)}
                                                                                    </div>
                                                                                ))}
                                                                            <footer
                                                                                className={styles.promptFooter}
                                                                                style={{
                                                                                    color: e.pk === "default@ai-works" ? "#19c37d" : "#2d3feb"
                                                                                }}
                                                                            >
                                                                                {hoveredTemplate === e.id && (
                                                                                    <Tooltip title={i18next.t("template.apply")}>
                                                                                        <CheckCircle
                                                                                            color="success"
                                                                                            onClick={() => {
                                                                                                setIsEdited(false);
                                                                                                if (clickedTemplateIndex !== undefined) {
                                                                                                    sendTemplateContents(templateContents[title][i], e.id);
                                                                                                }
                                                                                                snackbar();
                                                                                                setClickedItem("");
                                                                                            }}
                                                                                            style={{
                                                                                                cursor: "pointer",
                                                                                                fontSize: calculateFontsizeForFabIcons(),
                                                                                                float: "left",
                                                                                                marginTop: "5%"
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                )}
                                                                                <div style={{ marginTop: "5%" }}>
                                                                                    {e.pk === "default@ai-works" && e.template[0].isDefault === true ? (
                                                                                        <>
                                                                                            {i18next.t("template.systemTemplate")}{" "}
                                                                                            <span style={{ color: "red" }}>
                                                                                                ({i18next.t("template.default")})
                                                                                            </span>
                                                                                        </>
                                                                                    ) : e.pk === "default@ai-works" && e.template[0].isDefault === false ? (
                                                                                        i18next.t("template.systemTemplate")
                                                                                    ) : (
                                                                                        i18next.t("template.userTemplate")
                                                                                    )}
                                                                                </div>
                                                                            </footer>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </ElementDetails>
                                                ) : (
                                                    ""
                                                )}
                                            </Element>
                                        ))}
                                    </div>
                                )
                            )}
                            {deleteConfirmationDialogVisible && (
                                <Layer>
                                    <Popup role="dialog" aria-modal="true" onDismiss={handleCancelDelete} className={popupStyles.root}>
                                        <Overlay />
                                        <FocusTrapZone>
                                            <div role="document" className={popupStyles.content}>
                                                <div>
                                                    <IconButton style={{ color: "red" }} iconProps={{ iconName: "WarningSolid" }} />
                                                    <IconButton
                                                        style={{ color: "green", float: "right" }}
                                                        iconProps={{ iconName: "Cancel" }}
                                                        disabled={isDialogBusy}
                                                        onClick={handleCancelDelete}
                                                    />
                                                    <span
                                                        style={{
                                                            padding: "5px",
                                                            fontSize: window.innerWidth < 1000 ? "12px" : "15px",
                                                            fontWeight: "bold"
                                                        }}
                                                    >
                                                        {i18next.t("template.confirmDeleteTitle")}
                                                    </span>
                                                </div>
                                                <span style={{ marginTop: "25px", fontSize: window.innerWidth < 1000 ? "12px" : "15px" }}>
                                                    {i18next.t("template.confirmDeleteMessage")}
                                                </span>
                                                <div style={{ display: "flex", paddingTop: "25px", justifyContent: "flex-end" }}>
                                                    <PrimaryButton onClick={handleConfirmDelete} text={i18next.t("template.confirm")} disabled={isDialogBusy} />
                                                    <DefaultButton
                                                        onClick={handleCancelDelete}
                                                        text={i18next.t("template.cancel")}
                                                        disabled={isDialogBusy}
                                                        style={{ marginLeft: "10px" }}
                                                    />
                                                </div>
                                            </div>
                                        </FocusTrapZone>
                                    </Popup>
                                </Layer>
                            )}
                        </div>
                    )}

                    {isEdit || isAdd || isCopyAndCreateNew ? (
                        <div className={styles.templateActions}>
                            <>
                                <IconButton
                                    title={i18next.t("template.close")}
                                    style={{ color: "blueviolet", float: "right" }}
                                    disabled={isDialogBusy}
                                    iconProps={{ iconName: "Cancel", style: { fontSize: "20px" } }}
                                    onClick={() => {
                                        if (isDialogBusy) {
                                            return;
                                        }
                                        if (isAdd || isEdit || isCopyAndCreateNew) {
                                            checkContentsEntered();
                                            setShowAlert(false);
                                        } else {
                                            handleTemplateIconClick();
                                            setShowAlert(false);
                                        }
                                    }}
                                />
                                <IconButton
                                    style={{
                                        float: "right",
                                        marginTop: "20px",
                                        color: "blueviolet"
                                    }}
                                    title={i18next.t("template.save")}
                                    iconProps={{ iconName: "Save", style: { fontSize: "20px" } }}
                                    onClick={() => {
                                        handleSaveClick();
                                    }}
                                    disabled={
                                        isDialogBusy ||
                                        (templateContents[editingTemplate] &&
                                            templateContents[editingTemplate][editingIndex] &&
                                            (templateContents[editingTemplate][editingIndex].template[0]?.hasOwnProperty("promptInstructions")
                                                ? userPromptValues[0].prompt === templateContents[editingTemplate][editingIndex].template[0].prompt &&
                                                  userPromptValues[0].promptInstructions ===
                                                      templateContents[editingTemplate][editingIndex].template[0].promptInstructions
                                                : JSON.stringify(getPreviewAreaTextValue()) ===
                                                  JSON.stringify(templateContents[editingTemplate][editingIndex].template[0].previewPrompt)) &&
                                            userPromptValues[0].title.trim() === templateContents[editingTemplate][editingIndex]?.template[0].title.trim() &&
                                            selectedProduct === templateProduct &&
                                            (!isAdmin ||
                                                (userPromptValues[0].isSystemTemplate ===
                                                    templateContents[editingTemplate][editingIndex].template[0].isSystemTemplate &&
                                                    userPromptValues[0].isDefault === templateContents[editingTemplate][editingIndex].template[0].isDefault)) &&
                                            deepEqual(userPromptValues[0].fields, templateContents[editingTemplate][editingIndex]?.template[0].fields)) ||
                                        userPromptValues[0].title.trim().length === 0 ||
                                        templateProduct === `${i18next.t("chat.productFilterText")}`
                                    }
                                />
                                <div style={{ color: "blueviolet", float: "right" }}>
                                    <TooltipHost
                                        content={
                                            <>
                                                <p>
                                                    <b>{i18next.t("template.infoTitle")}</b>
                                                </p>
                                                <p>
                                                    {i18next.t("template.infoEg")} {"{ "}
                                                    <span style={{ color: "red" }}>{i18next.t("template.infoSubject")}</span>
                                                    {" }"}
                                                </p>
                                            </>
                                        }
                                        directionalHint={DirectionalHint.bottomRightEdge}
                                    >
                                        <IconButton
                                            style={{ color: "blueviolet", float: "right", marginTop: "20px" }}
                                            disabled={isDialogBusy}
                                            title={i18next.t("template.info")}
                                            iconProps={{ iconName: "Info", style: { fontSize: "20px" } }}
                                        />
                                    </TooltipHost>
                                </div>
                            </>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DynamicTemplatePrompt;
